// material-ui
import * as React from 'react';
import {
    InputLabel,
    MenuItem,
    FormControl,
    Select,
    Grid,
    Typography,
    Button,
    Stack
} from '@mui/material';

import dayjs from 'dayjs';
import { DatePicker } from 'antd';

// project imports
import MainCard from '../../components/MainCard';
import { FaDownload, FaFilter } from "react-icons/fa";
import { GrClear } from "react-icons/gr";
import { dateFormat } from '../../utils/constant';
import { Menu } from '@mui/material';
import DateRangeWithPresets from './DateRangeWithPresets';
import { getData } from '../../api/services';



const ReviewsFilter = ({ onFilterApply, defaultDateRange, onExportClick, onFilterClear, showSentimentOptions = false, showCategoryOptions = false }) => {

    const [country, setCountry] = React.useState('');
    const [location, setLocation] = React.useState('');
    const [company, setCompany] = React.useState('');
    const [source, setSource] = React.useState('');
    const [category, setCategory] = React.useState('');
    const [sentiment, setSentiment] = React.useState('');
    const [openCountry, setOpenCountry] = React.useState(false);
    const [openLocation, setOpenLocation] = React.useState(false);
    const [openCompany, setOpenCompany] = React.useState(false);
    const [openSource, setOpenSource] = React.useState(false);
    const [openSentimentCategory, setOpenSentimentCategory] = React.useState(false);
    const [openSentimentType, setOpenSentimentType] = React.useState(null);
    const [selectedDate, setSelectedDate] = React.useState(defaultDateRange);
    const [locationList, setLocationList] = React.useState([]);
    const [brandList, setBrandList] = React.useState([]);
    const [countryList, setCountryList] = React.useState([]);
    const [sourceList, setSourceList] = React.useState([]);
    const [sentimentCategoryList, setSentimentCategoryList] = React.useState([]);
    const sentimentOptions = ['positive', 'negative', 'neutral'];
    const [filterApplied, setFilterApplied] = React.useState(false);


    const handleChange = (setter) => (e) => setter(e.target.value);
    const handleOpen = (setter) => () => setter(true);
    const handleClose = (setter) => () => setter(false);

    // Usage
    const handleCountryChange = handleChange(setCountry);
    const handleLocationChange = handleChange(setLocation);
    const handleCompanyChange = handleChange(setCompany);
    const handleSourceChange = handleChange(setSource);
    const handleCategoryChange = handleChange(setCategory);
    const handleSentimentChange = handleChange(setSentiment);

    const handleCountryOpen = handleOpen(setOpenCountry);
    const handleCountryClose = handleClose(setOpenCountry);

    const handleLocationOpen = handleOpen(setOpenLocation);
    const handleLocationClose = handleClose(setOpenLocation);

    const handleCompanyOpen = handleOpen(setOpenCompany);
    const handleCompanyClose = handleClose(setOpenCompany);

    const handleSourceOpen = handleOpen(setOpenSource);
    const handleSourceClose = handleClose(setOpenSource);

    const handleCategoryOpen = handleOpen(setOpenSentimentCategory);
    const handleCategoryClose = handleClose(setOpenSentimentCategory);

    const handleSentimentOpen = handleOpen(setOpenSentimentType);
    const handleSentimentClose = handleClose(setOpenSentimentType);

    const { RangePicker } = DatePicker;
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleExportClose = (exportType = null) => {
        setAnchorEl(null);
        if (!exportType) return;
        onExportClick(exportType);
    };

    const handleRange = (start, end, preset) => {
        // start/end are Dayjs (or null), preset is "7" | "30" | "60" | "custom"
        // convert to ISO for API if needed:
        const from = start ? start.startOf("day").toISOString() : null;
        const to = end ? end.endOf("day").toISOString() : null;
        // console.log({ preset, from, to });
        setSelectedDate([dayjs(from, dateFormat), dayjs(to, dateFormat)]);
    };

    const fetchData = async (url, setter) => {
        const res = await getData('/review/' + url);
        setter(res?.data && res?.data[url] || []);
    }

    const handleFilterClear = () => {
        onFilterClear();
        setFilterApplied(false);
        setSelectedDate(defaultDateRange);
        setCountry('');
        setLocation('');
        setCompany('');
        setSource('');
        setCategory('');
        setSentiment('');
    }

    React.useEffect(() => {
        fetchData("locations", setLocationList)
        fetchData("brands", setBrandList)
        fetchData("countries", setCountryList)
        fetchData("sources", setSourceList)
        fetchData("categories", setSentimentCategoryList)
    }, []);


    return (
        <>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <MainCard>
                    <Grid container spacing={3}>
                        <div size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                            <Typography variant="h6" color="text.secondary">
                                Country
                            </Typography>
                            <FormControl sx={{ mt: 1, minWidth: 250 }}>
                                <InputLabel id="country-select-label">All Countries</InputLabel>
                                <Select
                                    labelId="country-select-label"
                                    id="country-select"
                                    open={openCountry}
                                    onClose={handleCountryClose}
                                    onOpen={handleCountryOpen}
                                    value={country}
                                    label="All Countries"
                                    onChange={handleCountryChange}
                                >
                                    <MenuItem value="">
                                        <em>All</em>
                                    </MenuItem>
                                    {countryList?.length && countryList?.map((countryItem) => (
                                        <MenuItem key={countryItem} value={countryItem?.toLowerCase()}>
                                            {countryItem?.toUpperCase()}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                        <div size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                            <Typography variant="h6" color="text.secondary">
                                Location
                            </Typography>
                            <FormControl sx={{ mt: 1, minWidth: 250 }}>
                                <InputLabel id="company-select-label">All Locations</InputLabel>
                                <Select
                                    labelId="company-select-label"
                                    id="company-select"
                                    open={openLocation}
                                    onClose={handleLocationClose}
                                    onOpen={handleLocationOpen}
                                    value={location}
                                    label="All Locations"
                                    onChange={handleLocationChange}
                                >
                                    <MenuItem value="">
                                        <em>All</em>
                                    </MenuItem>
                                    {locationList?.length && locationList?.map((locationItem) => (
                                        <MenuItem key={locationItem} value={locationItem}>
                                            {locationItem}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                        <div size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                            <Typography variant="h6" color="text.secondary">
                                Company
                            </Typography>
                            <FormControl sx={{ mt: 1, minWidth: 250 }}>
                                <InputLabel id="company-select-label">All Companies</InputLabel>
                                <Select
                                    labelId="company-select-label"
                                    id="company-select"
                                    open={openCompany}
                                    onClose={handleCompanyClose}
                                    onOpen={handleCompanyOpen}
                                    value={company}
                                    label="All Companies"
                                    onChange={handleCompanyChange}
                                >
                                    <MenuItem value="">
                                        <em>All</em>
                                    </MenuItem>
                                    {brandList?.length && brandList?.map((companyItem) => (
                                        <MenuItem key={companyItem.code} value={companyItem.code}>
                                            {companyItem.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                        <div size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                            <Typography variant="h6" color="text.secondary">
                                Date Range
                            </Typography>
                            <FormControl sx={{ mt: 1, minWidth: 250 }}>
                                <DateRangeWithPresets onChange={handleRange} selectedDate={selectedDate} />
                            </FormControl>
                        </div>
                        <div size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                            <Typography variant="h6" color="text.secondary">
                                Source
                            </Typography>
                            <FormControl sx={{ mt: 1, minWidth: 250 }}>
                                <InputLabel id="source-select-label">All Sources</InputLabel>
                                <Select
                                    labelId="source-select-label"
                                    id="source-select"
                                    open={openSource}
                                    onClose={handleSourceClose}
                                    onOpen={handleSourceOpen}
                                    value={source}
                                    label="All Sources"
                                    onChange={handleSourceChange}
                                >
                                    <MenuItem value="">
                                        <em>All</em>
                                    </MenuItem>
                                    {sourceList && sourceList?.map((sourceItem) => (
                                        <MenuItem key={sourceItem} value={sourceItem}>
                                            {sourceItem}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                        {showCategoryOptions ?
                            <div size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                                <Typography variant="h6" color="text.secondary">
                                    Category
                                </Typography>
                                <FormControl sx={{ mt: 1, minWidth: 250 }}>
                                    <InputLabel id="source-select-label">All Categories</InputLabel>
                                    <Select
                                        labelId="source-select-label"
                                        id="source-select"
                                        open={openSentimentCategory}
                                        onClose={handleCategoryClose}
                                        onOpen={handleCategoryOpen}
                                        value={category}
                                        label="All Categories"
                                        onChange={handleCategoryChange}
                                    >
                                        <MenuItem value="">
                                            <em>All</em>
                                        </MenuItem>
                                        {sentimentCategoryList && sentimentCategoryList?.map((categoryItem) => (
                                            <MenuItem key={categoryItem} value={categoryItem}>
                                                {categoryItem}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                            : ''
                        }
                        {showSentimentOptions ?
                            <div size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                                <Typography variant="h6" color="text.secondary">
                                    Sentiment Type
                                </Typography>
                                <FormControl sx={{ mt: 1, minWidth: 250 }}>
                                    <InputLabel id="source-select-label">All Sentiments</InputLabel>
                                    <Select
                                        labelId="source-select-label"
                                        id="source-select"
                                        open={openSentimentType}
                                        onClose={handleSentimentClose}
                                        onOpen={handleSentimentOpen}
                                        value={sentiment}
                                        label="All Sentiments"
                                        onChange={handleSentimentChange}
                                    >
                                        <MenuItem value="">
                                            <em>All</em>
                                        </MenuItem>
                                        {sentimentOptions && sentimentOptions?.map((item) => (
                                            <MenuItem key={item} value={item}>
                                                {item}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                            : ''
                        }
                    </Grid>
                    <Grid container spacing={3} sx={{ mt: 2 }}>
                        <Stack direction="row" spacing={2}>
                            <Button color="primary" variant="outlined" startIcon={<FaDownload />}
                                aria-controls={open ? 'demo-positioned-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={open ? 'true' : undefined}
                                onClick={handleClick}
                            >
                                Export (PDF/CSV)
                            </Button>
                            <Menu
                                id="demo-positioned-menu"
                                aria-labelledby="demo-positioned-button"
                                anchorEl={anchorEl}
                                open={open}
                                onClose={handleExportClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                            >
                                <MenuItem style={{ width: 165 }} onClick={() => { handleExportClose('csv'); }}>CSV Export</MenuItem>
                                <MenuItem style={{ width: 165 }} onClick={() => { handleExportClose('pdf'); }}>PDF Export</MenuItem>
                            </Menu>
                            <Button
                                color="primary"
                                variant="contained"
                                endIcon={<FaFilter />}
                                onClick={() => {
                                    setFilterApplied(true); onFilterApply(country, location, company, source, selectedDate, category, sentiment);
                                }}>
                                Apply Filters
                            </Button>
                            {
                                filterApplied ?
                                    <Button color="primary" variant="outlined" endIcon={<GrClear style={{ fontSize: 18 }} />} onClick={() => { setFilterApplied(false); handleFilterClear() }}>
                                        Clear Filters
                                    </Button> : ''
                            }
                        </Stack>
                    </Grid>
                </MainCard>
            </Grid>
        </>
    )
}

export default ReviewsFilter